"use client";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"; // 👈 Import useRef
import { apiFetch } from "@/utils/api";
import ErrorState from "@/components/ui/ErrorState";
import EmptyState from "@/components/ui/EmptyState";
import Link from "next/link";
import Image from "next/image";
import { useInView } from "react-intersection-observer";
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from "@/utils/icons";
import Carousel from "@/components/ui/Carousel";

const ALPHABETS: string[] = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const PAGE_SIZE = 24;

// --- INTERFACES ---
type Category = {
  _id?: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  // Fields to display coupon and offer counts
  coupons?: number;
  offersCount?: number;
  isActive?: boolean;
};

type Store = {
  _id?: string;
  name: string;
  slug: string;
  offersCount?: number;
  coupons?: number;
};

type ApiResponse = {
  success: boolean;
  data: Category[];
};

const fetchCountsForCategory = async (slug: string): Promise<{ coupons: number, offersCount: number }> => {
  try {
    // Use the existing, working endpoint from the single category page
    const storesResponse = await apiFetch<{ success: boolean; data: Store[] }>(
      `/categories/slug/${slug}/stores-with-offer-count`
    );

    if (storesResponse.success && Array.isArray(storesResponse.data)) {
      // Sum up the counts from all stores in this category
      const totalCoupons = storesResponse.data.reduce((sum, store) => sum + (store.coupons || 0), 0);
      const totalOffers = storesResponse.data.reduce((sum, store) => sum + (store.offersCount || 0), 0);
      return { coupons: totalCoupons, offersCount: totalOffers };
    }
  } catch (e) {
    console.error(`Failed to fetch counts for ${slug}`, e);
  }
  return { coupons: 0, offersCount: 0 };
};

// Breadcrumb component for cleaner look (used inline below)
const Breadcrumb = () => (
  <div className="md:block hidden w-full">
    <div className="max-w-7xl mx-auto py-2 px-2 text-white">
      <nav className="flex" aria-label="Breadcrumb">
        <ol role="list" className="flex items-center space-x-1 text-xs">
          <li>
            <Link href="/" className="font-medium">
              Home
            </Link>

          </li>
          {true && (
            <svg
              className="flex-shrink-0 w-3 h-3 ml-2 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"></path>
            </svg>
          )}
          <li>
            <span className="text-gray-400"></span>
          </li>
          <li>
            <span>
              Categories
            </span>
          </li>
        </ol>
      </nav>
    </div>
  </div>
);


export default function CategoriesPage() {
  // State management
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [categories, setCategories] = useState<Category[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [trending, setTrending] = useState<Category[]>([]);
  const [stats, setStats] = useState({ total: 0 });

  // Filter states
  const [search, setSearch] = useState("");
  const [alpha, setAlpha] = useState("");

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [lastVerified, setLastVerified] = useState<string>("");
  const trendingCategoriesRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for infinite scroll
  const { ref: loadMoreRef, inView } = useInView({ triggerOnce: false });

  // Load initial data only once
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Fetch ALL categories (Still using ?withCounts=true for future compatibility)
        const allCategoriesResponse = await apiFetch<ApiResponse>(
          "/categories/?withCounts=true"
        );

        const fetchedData: Category[] = (
          allCategoriesResponse?.success && Array.isArray(allCategoriesResponse.data)
            ? allCategoriesResponse.data
            : []
        );

        // 2. Filter for active categories
        const activeCategories = fetchedData.filter(cat => cat.isActive);

        // 3. WORKAROUND: Fetch counts for ALL active categories concurrently 💥
        const countPromises = activeCategories.map(cat => fetchCountsForCategory(cat.slug));
        const allCounts = await Promise.all(countPromises);

        // 4. Merge the fetched counts into the category data
        const categoriesWithCounts: Category[] = activeCategories.map((cat, index) => ({
          ...cat,
          coupons: allCounts[index].coupons,
          offersCount: allCounts[index].offersCount,
        }));

        setAllCategories(categoriesWithCounts);
        setCategories(categoriesWithCounts);
        setStats({ total: categoriesWithCounts.length });

        // 5. Load trending categories (and apply the new merged counts)
        let trendingData: Category[] = [];
        try {
          const trendingResponse = await apiFetch<ApiResponse>(
            "/categories/?trending=true&isActive=true&withCounts=true"
          );

          const rawTrending = trendingResponse.success && trendingResponse.data.length > 0
            ? trendingResponse.data.filter(cat => cat.isActive)
            : categoriesWithCounts.slice(0, 8);

          // Merge counts into the raw trending data by matching slug
          trendingData = rawTrending.map(tCat => {
            const mergedCat = categoriesWithCounts.find(c => c.slug === tCat.slug);
            return {
              ...tCat,
              // Use the calculated count, fallback to existing or 0
              coupons: mergedCat?.coupons || tCat.coupons || 0,
              offersCount: mergedCat?.offersCount || tCat.offersCount || 0,
            };
          });

        } catch {
          // Fallback uses the main counted categories array
          trendingData = categoriesWithCounts.slice(0, 8);
        }

        setTrending(trendingData.slice(0, 8));

        // Set last verified date
        setLastVerified(
          new Date().toLocaleDateString("en-IN", {
            weekday: "short",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        );
      } catch (err) {
        console.error("Failed to load initial data:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load categories";
        setError(errorMessage);
        setStats({ total: 0 });
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []); // Run only once

  // Client-side filtering (similar to offers page)
  const filteredCategories = useMemo(() => {
    return allCategories.filter((cat) => {
      // Search filter
      const matchesSearch =
        !search ||
        cat.name.toLowerCase().includes(search.toLowerCase()) ||
        (cat.description &&
          cat.description.toLowerCase().includes(search.toLowerCase()));

      // Alphabet filter
      const matchesAlpha = !alpha || cat.name.charAt(0).toUpperCase() === alpha;

      return matchesSearch && matchesAlpha;
    });
  }, [allCategories, search, alpha]);

  // Paginated results for display
  const paginatedCategories = useMemo(() => {
    const endIndex = page * PAGE_SIZE;
    return filteredCategories.slice(0, endIndex);
  }, [filteredCategories, page]);

  // Update hasMore based on pagination 
  useEffect(() => {
    setHasMore(paginatedCategories.length < filteredCategories.length);
  }, [paginatedCategories.length, filteredCategories.length]);

  // Reset page when filters change 
  useEffect(() => {
    setPage(1);
  }, [search, alpha]);

  // Load more categories when scrolling 
  useEffect(() => {
    if (!hasMore || isFetchingMore || loading || !inView) return;

    setIsFetchingMore(true);
    // Simulate loading delay for smooth UX
    setTimeout(() => {
      setPage((prev) => prev + 1);
      setIsFetchingMore(false);
    }, 300);
  }, [hasMore, isFetchingMore, loading, inView]);

  // Handle retry 
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    // Reload page to retry
    window.location.reload();
  }, []);

  // Clear search 
  const clearSearch = useCallback(() => {
    setSearch("");
  }, []);

  // Reset filters 
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const resetFilters = useCallback(() => {
    setSearch("");
    setAlpha("");
  }, []);

  const scrollTrendingCategories = (direction: 'left' | 'right') => {
    if (trendingCategoriesRef.current) {
      // Use a fixed scroll amount (e.g., 300px) for smooth UX
      const scrollAmount = 300;
      const currentScroll = trendingCategoriesRef.current.scrollLeft;

      if (direction === 'left') {
        trendingCategoriesRef.current.scroll({
          left: currentScroll - scrollAmount,
          behavior: 'smooth'
        });
      } else {
        trendingCategoriesRef.current.scroll({
          left: currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }
    }
  };

  const renderLoadingSkeleton = () => (
    <div className="bg-gray-50 pt-0 lg:pb-3 min-h-screen">
      {/* Placeholder for the gradient banner area */}
      <div className="md:block hidden bg-gray-200 text-white relative h-88 sm:h-72 md:h-68"></div>

      <div className="md:block hidden w-full bg-gray-100 border-b border-gray-200 h-10 animate-pulse">
        <div className="max-w-7xl mx-auto py-3 px-4 md:px-6 lg:px-8">
          <div className="w-40 h-3 bg-gray-300 rounded" />
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 lg:p-8 py-4 px-2 pt-0 -mt-0 md:-mt-68 relative z-10 animate-pulse">
        <aside className="w-full md:w-64 space-y-4 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-lg p-6 h-64 border border-primary-100">
            <div className="w-20 h-20 bg-gray-200 rounded-lg mx-auto mb-4" />
            <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-4" />
            <div className="h-4 bg-gray-100 rounded w-1/3 mx-auto" />
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 h-64 border border-primary-100">
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-4" />
            <div className="space-y-3">
              <div className="h-3 bg-gray-100 rounded w-4/5" />
              <div className="h-3 bg-gray-100 rounded w-3/5" />
              <div className="h-3 bg-gray-100 rounded w-4/5" />
            </div>
          </div>
        </aside>
        <main className="flex-1 space-y-6">
          {/* Header placeholder */}
          <div className="flex flex-col gap-1 mb-8 py-4 md:py-0 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 mt-5 items-center justify-center flex-shrink-0 bg-white rounded-xl border border-gray-100 shadow-lg hidden md:flex" />
              <div className="h-8 w-1/3 bg-gray-200 rounded mt-5" />
            </div>
          </div>
          <div className="h-8 w-1/2 bg-gray-200 rounded mb-6" />
          <div className="mb-8">
            <div className="font-semibold text-lg text-gray-200 mb-3">Trending Categories</div>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {Array.from({ length: 6 }, (_, i) => (
                <div
                  key={i}
                  // Skeleton uses the responsive classes
                  className="min-w-[140px] sm:min-w-[180px] bg-white rounded-xl shadow flex flex-col items-center px-4 py-3 border border-gray-100 animate-pulse"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full mb-2" />
                  <div className="h-4 w-16 bg-gray-200 rounded mb-1" />
                  <div className="h-3 w-12 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          </div>
          {/* Search/Filter Placeholder */}
          <div className="h-12 bg-gray-100 rounded-xl mb-6" />
          <div className="flex flex-wrap gap-2 mb-6">
            {Array.from({ length: 8 }, (_, i) => (
              <div
                key={i}
                className="w-12 h-8 bg-gray-100 rounded-full animate-pulse"
              />
            ))}
          </div>
          {/* FIX: Changed grid layout to match the live component: lg:grid-cols-3 */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-6">
            {Array.from({ length: 8 }, (_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow flex flex-col items-center px-4 py-6 border border-gray-100 animate-pulse"
              >
                <div className="w-12 h-12 bg-gray-200 rounded-lg mb-3" />
                <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-16 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );

  const renderLoadingCards = () =>
    Array.from({ length: 6 }, (_, i) => (
      <div
        key={`skeleton-${i}`}
        className="bg-white rounded-xl shadow flex flex-col items-center px-4 py-6 border border-gray-100 animate-pulse"
      >
        <div className="w-12 h-12 bg-gray-200 rounded-lg mb-3" />
        <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-16 bg-gray-100 rounded" />
      </div>
    ));

  if (loading) return renderLoadingSkeleton();

  if (error) {
    return <ErrorState message={error} onRetry={handleRetry} />;
  }

  return (
    <div className="bg-gray-50 pt-0 lg:pb-3 min-h-screen">
      {/* Hero Section: Gradient Banner  */}
      <div className="md:block hidden bg-gradient-to-r from-primary-600 to-purple-700 text-white relative h-88 sm:h-72 md:h-48">
      </div>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6 lg:p-8 py-4 px-2 pt-0 -mt-0 md:-mt-44 lg:-mt-48 relative z-10">
        <aside className="w-full md:w-64 flex-shrink-0">
          <Breadcrumb />

          {/* 1. Mobile UI Header  */}
          <div className="md:hidden py-3">
            <div className="flex w-full bg-primary-200 rounded-xl shadow-xl p-4 border border-primary-100 items-center justify-between overflow-hidden">

              {/* Left Column: Image/Icon */}
              <div className="w-20 h-20 flex-shrink-0 bg-slate-50 rounded-xl shadow flex items-center justify-center">
                <Image
                  src={"/assets/categoriesiconpng.png"}
                  alt={"ALL CATEGORIES Icon"}
                  width={64}
                  height={64}
                  className="object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/OfferCodeFlight.jpg";
                  }}
                />
              </div>

              {/* Right Column: Text and Stats (Compact Mobile Layout) */}
              <div className="flex flex-col items-start pl-4 flex-1">
                <div className="text-xl font-bold text-gray-900 mb-1">
                  CATEGORIES
                </div>
                {/* Reduced font size for description */}
                <div className="text-gray-600 text-[10.5px] sm:mt-1 lg:mt-0 md:ml-18">
                  Browse and discover categories for all your favorite stores, offers, and deals. Use the search and alphabet filter to quickly find what you need.
                </div>

                {/* Combined Stats Row */}
                <div className="flex items-baseline gap-2 mb-2">
                  <div className="text-sm text-gray-700">Total Categories:</div>
                  <div className="text-2xl font-bold text-primary-600">
                    {filteredCategories.length.toLocaleString()}
                  </div>

                </div>

                {/* Verified On */}
                <div className="text-[10px] flex items-center gap-1 text-green-600">
                  <CheckCircleIcon className="w-4 h-4" /> Verified On:{" "}
                  {lastVerified}
                </div>
              </div>
            </div>
          </div>

          {/* 2. Desktop UI Sidebar Stats */}
          <div className="hidden md:flex flex-col w-full bg-white rounded-xl shadow-lg p-6 mb-4 border border-primary-100 items-start justify-start overflow-hidden">

            <div className="flex flex-col items-center w-full mb-4">
              <Image
                src={"/assets/categoriesiconpng.png"}
                alt={"ALL CATEGORIES"}
                width={80}
                height={80}
                className="object-contain w-20 h-20 rounded-xl shadow-md mb-2"
                onError={(e) => {
                  e.currentTarget.src = "/assets/OfferCodeFlight.jpg";
                }}
              />
              <div className="text-xl font-bold text-center text-gray-900 mt-2">
                ALL CATEGORIES
              </div>
            </div>

            {/* Total Deals Summary */}
            <div className="w-full text-center py-3 border-y border-gray-200 mb-4">
              <div className="text-sm font-medium text-gray-500">Total Categories</div>
              <div className="text-4xl font-extrabold text-primary-600">
                {stats.total.toLocaleString()}
              </div>
            </div>

            {/* Verification Status */}
            <div className="text-xs flex items-center justify-center gap-1 text-green-600 font-medium">
              <CheckCircleIcon className="w-4 h-4" /> Verified On:{" "}
              <span className="text-gray-500">{lastVerified}</span>
            </div>
          </div>


          {/* 3. Desktop Sidebar Navigation/Ad */}
          <aside className="hidden md:block w-full md:w-64 flex-shrink-0 bg-white rounded-xl shadow-lg p-6 mb-6 md:mb-0 border border-primary-100">
            <nav className="flex flex-col gap-2 text-sm text-gray-700">
              <Link className="hover:text-primary-600 font-medium transition-colors" href="/category">
                See All Categories
              </Link>
              <Link className="hover:text-primary-600 font-medium transition-colors" href="/store">
                See All Stores
              </Link>
              <Link className="hover:text-primary-600 font-medium transition-colors" href="/offers">
                See All Offers
              </Link>
              {/* <Link className="hover:text-primary-600 font-medium transition-colors" href="/cashback">
                See All CashBack
              </Link>
              <Link className="hover:text-primary-600 font-medium transition-colors" href="/bank-wallet-offers">
                See All Banks
              </Link> */}
            </nav>
            <div className="mt-8 w-full flex justify-center">
              <div className="w-full">
                <Carousel orientation="vertical" />
              </div>
            </div>
          </aside>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden md:pt-6">
          {/* Desktop Main Header */}
          <div className="hidden md:flex flex flex-col gap-1 mb-6 py-4 md:py-0 text-white md:text-gray-900 px-2 md:px-0">
            {/* <div className=" items-center gap-4">
              <div className=" w-14 h-14 mt-5 items-center justify-center flex-shrink-0 bg-white rounded-xl border border-gray-100 shadow-lg">
                <Image
                  src={"/assets/categoriesiconpng.png"}
                  alt={"Categories Icon"}
                  width={56}
                  height={56}
                  className="object-contain max-h-12"
                  onError={(e) => {
                    e.currentTarget.src = "/assets/OfferCodeFlight.jpg";
                  }}
                />
              </div>
              <h1 className="lg:text-4xl text-2xl font-extrabold md:font-bold text-white">
                Categories
              </h1>
              <div className="h-0.5 bg-primary-500 mt-2  rounded-full md:bg-white" />

            </div> */}
            <div className="inline-block">
              <h1 className="lg:text-4xl text-2xl font-extrabold md:font-bold text-white">Categories</h1>
            </div>

            <div className="text-white/80 text-sm md:text-base sm:mt-2 lg:mt-0">
              Browse and discover categories for all your favorite stores, offers, and deals. Use the search and alphabet filter to quickly find what you need.
            </div>
          </div>

          <div className="mb-4 lg:pt-7 md:pt-7 flex items-center justify-between">
            <h2 className="text-lg md:text-xl lg:text-xl xl:text-xl font-bold mb-4 text-gray-800 px-2 md:px-0 md:pt-8 xl:pt-5 lg:pt-5 pt-0">
              Trending Categories
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => scrollTrendingCategories('left')}
                className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
                aria-label="Scroll Offers Left"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => scrollTrendingCategories('right')}
                className="p-1 bg-gray-100 rounded-full shadow-md text-gray-600 hover:bg-gray-200 transition-colors"
                aria-label="Scroll Offers Right"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Trending Categories - Offer/Coupon Counts */}
          {trending.length > 0 && (
            <section className="lg:mb-8 md:mb-8 mb-5 xl:mb-8 px-2 md:px-0">
              <div className="relative group">
                <div
                  ref={trendingCategoriesRef}
                  className="flex gap-4 lg:gap-5 overflow-x-auto pb-2 w-full scrollbar-hide"
                  style={{ scrollBehavior: "smooth" }}
                >
                  {trending.map((cat) => (
                    <Link
                      key={cat._id || cat.slug}
                      href={`/category/${cat.slug}`}
                      className="min-w-[140px] w-[140px] sm:min-w-[180px] sm:w-[180px] bg-white rounded-xl shadow-md flex flex-col items-center p-0 overflow-hidden border border-gray-100 hover:shadow-lg hover:scale-[1.01] transition-all duration-200"
                    >
                      <div className="flex flex-col items-center w-full pt-3 pb-2 px-2 sm:pt-4 sm:px-3">
                        <Image
                          src={
                            cat.icon && cat.icon.length > 2
                              ? cat.icon
                              : "/assets/OfferCodeFlight.jpg"
                          }
                          alt={cat.name}
                          width={72}
                          height={72}
                          className="h-[56px] w-[56px] sm:h-[72px] sm:w-[72px] rounded-full object-cover mb-2 sm:mb-3 shadow-inner" // Circular icon style
                          onError={(e) => {
                            e.currentTarget.src = "/assets/OfferCodeFlight.jpg";
                          }}
                        />
                        {/* Name adjusted for smaller screens */}
                        <div className="font-semibold lg:text-base md:text-base xl:text-base text-sm text-gray-800 text-center truncate w-full">
                          {cat.name}
                        </div>
                        {/* <div className="text-xs text-gray-800 text-center w-full">
                        {cat.description}
                      </div> */}
                      </div>

                      {/* Bottom Section: Offer and Coupon Counts (Text size reduced for mobile) */}
                      <div className="w-full bg-gray-50 border-t border-gray-100 flex justify-around p-2 text-center text-sm font-medium">
                        <div className="flex flex-col items-center border-r border-gray-200 flex-1 pr-2">
                          {/* Number size reduced on mobile */}
                          <span className="md:text-sm lg:text-sm xl:text-sm text-xs font-bold text-primary-600">
                            {cat.coupons?.toLocaleString() || "0"}
                          </span>
                          <span className="text-xs text-gray-500">Coupons</span>
                        </div>
                        <div className="flex flex-col items-center flex-1 pl-2">
                          {/* Number size reduced on mobile */}
                          <span className="md:text-sm lg:text-sm xl:text-sm text-xs  font-bold text-primary-600">
                            {(Number(cat.offersCount) - Number(cat.coupons)) || "0"}
                          </span>
                          <span className="text-xs text-gray-500">Offers</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          <div className="bg-white p-2 lg:p-6 rounded-xl shadow-lg border border-gray-100 w-full mb-6">
            {/* Title adjusted for mobile */}
            <h2 className="lg:text-xl md:text-xl xl:text-xl text-lg font-bold mb-4 text-gray-800 pt-4 px-2 md:px-0">
              Search & Filter Categories
            </h2>

            {/* Search */}
            <div className="flex flex-col md:flex-row md:items-end gap-4 mb-6 text-gray-700 w-full">
              <div className="flex-1 w-full">
                <label htmlFor="category-search" className="sr-only">
                  Search categories
                </label>
                <div className="relative w-full">
                  <input
                    id="category-search"
                    type="text"
                    placeholder="Search categories by name or description..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-500 text-base bg-white transition-all pr-10 shadow-sm"
                    autoComplete="off"
                    aria-label="Search categories"
                  />
                  {search && (
                    <button
                      type="button"
                      aria-label="Clear search"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      onClick={clearSearch}
                      tabIndex={0}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Alphabet Filter  */}
            <div className="flex text-[10px] md:text-sm lg:text-sm xl:text-sm flex-wrap gap-1 mb-6 overflow-x-auto w-full">
              <button
                className={`px-1 md:px-2 lg:px-2 py-1 md:py-2 lg:py-2 font-medium transition-all ${!alpha
                  ? "text-primary-600"
                  : "hover:bg-primary-50"
                  }`}
                onClick={() => setAlpha("")}
              >
                All
              </button>
              {ALPHABETS.map((a) => (
                <button
                  key={a}
                  className={`px-1 lg:px-2 md:px-2 py-1 md:py-2 lg:py-2 font-medium transition-all ${alpha === a
                    ? "text-primary-600 "
                    : "hover:bg-primary-50"
                    }`}
                  onClick={() => setAlpha(a)}
                >
                  {a}
                </button>
              ))}
            </div>

            {/* Results count  */}
            {(search || alpha) && (
              <div className="text-sm text-gray-600 mb-4">
                Showing **{filteredCategories.length.toLocaleString()}** categories of **{stats.total.toLocaleString()}** total
              </div>
            )}

            {/* Categories Grid */}
            {paginatedCategories.length === 0 ? (
              <EmptyState message="No categories found matching your filter." emoji="📂" />
            ) : (
              <div
                role="list"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-6"
              >
                {paginatedCategories.map((cat, i) => (
                  <div
                    key={cat._id || `category-${i}`}
                    tabIndex={0}
                    aria-label={`${cat.name} category`}
                    role="listitem"
                    className="focus:outline-none focus:ring-2 focus:ring-primary-400 rounded-xl"
                  >
                    <Link
                      href={`/category/${cat.slug}`}
                      className="flex flex-col items-center bg-white rounded-xl shadow-sm p-0 w-full border border-gray-100 hover:shadow-xl hover:scale-[1.02] hover:border-primary-500 focus-within:shadow-xl focus-within:border-primary-500 transition-all duration-200 cursor-pointer overflow-hidden"
                    >
                      {/* Card Content Top Section */}
                      <div className="flex flex-col items-center w-full pt-4 pb-2 px-3 lg:h-[10vw]">
                        <Image
                          src={
                            cat.icon && cat.icon.length > 2
                              ? cat.icon
                              : "/assets/OfferCodeFlight.jpg"
                          }
                          alt={cat.name}
                          width={80}
                          height={64}
                          className="mb-3 object-contain rounded-lg h-14 w-24 hidden md:block"
                          onError={(e) => {
                            e.currentTarget.src = "/assets/OfferCodeFlight.jpg";
                          }}
                        />
                        <div className="font-semibold text-sm text-gray-800 lg:mb-1 md:mb-1 lg:text-center truncate w-full px-1">
                          {cat.name}
                        </div>
                        <div className="hidden md:block">
                        {cat.description && (
                          <div className="text-[10px] text-gray-500 mt-1 text-center line-clamp-2 px-1 ">
                            {cat.description}
                          </div>
                        )}
                        </div>
                      </div>

                      {/* Coupon/Offer Counts  */}
                      <div className="w-full bg-gray-50 border-t border-gray-100 flex justify-around lg:p-2 md:p-2 p-1 text-center text-sm font-medium mt-auto">
                        <div className="flex flex-col items-center border-r border-gray-200 flex-1 pr-2">
                          <span className="md:text-sm lg:text-sm xl:text-sm text-xs font-bold text-primary-600">
                            {cat.coupons?.toLocaleString() || "0"}
                          </span>
                          <span className="text-xs text-gray-500">Coupons</span>
                        </div>
                        <div className="flex flex-col items-center flex-1 pl-2">
                          <span className="md:text-sm lg:text-sm xl:text-sm text-xs font-bold text-primary-600">
                            {(Number(cat.offersCount) - Number(cat.coupons)) || "0"}
                          </span>
                          <span className="text-xs text-gray-500">Offers</span>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
                {isFetchingMore && renderLoadingCards()}
              </div>
            )}
          </div>

          {/* Load more indicator  */}
          {hasMore && !isFetchingMore && (
            <div ref={loadMoreRef} className="text-center py-6">
              <span className="text-gray-400">Scroll to load more...</span>
            </div>
          )}

          {isFetchingMore && (
            <div className="text-center py-6">
              <span className="animate-pulse text-primary-600 font-medium">
                Loading more categories...
              </span>
            </div>
          )}

          {!hasMore && paginatedCategories.length > 0 && (
            <div className="text-center py-4 text-gray-500">
              You&apos;ve reached the end of the results!
            </div>
          )}
        </main>
      </div>
    </div>
  );
}